import { createAsyncThunk } from "@reduxjs/toolkit";
import SubscriptionApiService from "../apiServices/SubscriptionApiService";
import {
  CancelSubscriptionPayload,
  SubscribeBusinessPayload,
} from "../../interfaces/SubscriptionObject";

/** No known backend error codes to map yet (unlike onboarding) — surface
 *  whatever message ApiEnvelopeError/network failure produced. */
function toFriendlySubscriptionError(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

export const fetchUserSubscriptions = createAsyncThunk(
  "subscriptions/fetchUserSubscriptions",
  async (_: void, { rejectWithValue }) => {
    try {
      return await SubscriptionApiService.getUserSubscriptions();
    } catch (error) {
      return rejectWithValue(toFriendlySubscriptionError(error));
    }
  }
);

/** Starts (or re-triggers) auto-pay setup for one business's subscription.
 *  Per-business loading key is set by the slice from meta.arg.businessId, so
 *  subscribing one business doesn't disable another's button. */
export const subscribeBusiness = createAsyncThunk(
  "subscriptions/subscribeBusiness",
  async (payload: SubscribeBusinessPayload, { rejectWithValue }) => {
    try {
      return await SubscriptionApiService.subscribeBusiness(
        payload.businessId,
        payload.planCode
      );
    } catch (error) {
      return rejectWithValue(toFriendlySubscriptionError(error));
    }
  }
);

/** Cancels a subscription. Per-business loading key is set by the slice from
 *  meta.arg.businessId, so cancelling one subscription doesn't disable
 *  another business's buttons. */
export const cancelSubscription = createAsyncThunk(
  "subscriptions/cancelSubscription",
  async (payload: CancelSubscriptionPayload, { rejectWithValue }) => {
    try {
      return await SubscriptionApiService.cancelSubscription(payload);
    } catch (error) {
      return rejectWithValue(toFriendlySubscriptionError(error));
    }
  }
);

/** Fetches the catalog of plans a business can subscribe to. Global (not
 *  per-business) — same loading/error key shape as fetchUserSubscriptions. */
export const fetchSubscriptionPlans = createAsyncThunk(
  "subscriptions/fetchSubscriptionPlans",
  async (_: void, { rejectWithValue }) => {
    try {
      return await SubscriptionApiService.getSubscriptionPlans();
    } catch (error) {
      return rejectWithValue(toFriendlySubscriptionError(error));
    }
  }
);

const SubscriptionThunk = {
  fetchUserSubscriptions,
  subscribeBusiness,
  cancelSubscription,
  fetchSubscriptionPlans,
};

export default SubscriptionThunk;
